/**
 * =============================================================
 * AADHAV ACADEMY – HOMEWORK SUBMISSION & EVALUATION API
 * Role: Student / Teacher / Admin
 * Scope: Multi-tenant Submission Pipeline & Grade Book Metric Synergies
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */
const HOMEWORK_SUBMISSION_ENDPOINT = "/admin/homework/homework_submission.php";

/* =============================================================
   GET – Fetch All Submission Records Across Tenant Context
   Context: Used exclusively by Admins/Teachers to view a global log
============================================================= */
export const getAllTenantSubmissions = async (options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      HOMEWORK_SUBMISSION_ENDPOINT,
      null,
      "Fetch All Tenant Submission Records",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to retrieve total tenant submissions view",
        data: [],
      };
    }

    return {
      success: true,
      data: res.data || [],
    };
  } catch (error) {
    console.error("Global Tenant Fetch Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected tenant operational pipeline mismatch",
      data: [],
    };
  }
};

/* =============================================================
   GET – Fetch Single Submission Tracking Record Detail
   Context: Used by Student/Teacher/Admin to view a complete submission
============================================================= */
export const getHomeworkSubmissionById = async (id, options = {}) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Submission Reference ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${HOMEWORK_SUBMISSION_ENDPOINT}?id=${id}`,
      null,
      "Fetch Submission Details By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch submission details record",
      };
    }

    return {
      success: true,
      data: res.data || null,
    };
  } catch (error) {
    console.error("Fetch Submission By ID Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected error occurred during layout read",
    };
  }
};

/* =============================================================
   POST – Student Submission Commit
   Payload: { student_notes }
============================================================= */
export const submitStudentHomework = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      HOMEWORK_SUBMISSION_ENDPOINT,
      payload,
      "Student - Push Homework Submission",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Submission rejected by institutional node constraints",
      };
    }

    return {
      success: true,
      message: res.message || "Homework package dispatched and logged successfully",
    };
  } catch (error) {
    console.error("Student Homework Submit Action Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected structural handshake anomaly",
    };
  }
};

/* =============================================================
   PUT – Teacher Evaluation & Feedback Synchronization Engine
   Payload: { student_homework_status_id, submission_status, teacher_feedback }
============================================================= */
export const evaluateStudentSubmission = async (payload, options = {}) => {
  try {
    if (!payload?.student_homework_status_id) {
      return {
        success: false,
        message: "Missing tracking identifier or evaluation arguments",
      };
    }

    const res = await apiCall(
      "PUT",
      HOMEWORK_SUBMISSION_ENDPOINT,
      payload,
      "Teacher - Synchronize Roster Evaluation Metrics",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to finalize evaluation metrics record",
      };
    }

    return {
      success: true,
      message: res.message || "Student evaluation score maps synchronized cleanly",
    };
  } catch (error) {
    console.error("Teacher Evaluation Sync Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected data flow validation block",
    };
  }
};

/* =============================================================
   PUT – Admin Intervention & Note Override Engine
   Payload: { student_homework_status_id, admin_notes, submission_status (optional) }
============================================================= */
export const adminUpdateSubmission = async (payload, options = {}) => {
  try {
    if (!payload?.student_homework_status_id) {
      return {
        success: false,
        message: "Missing tracking identifier row parameter for admin intervention",
      };
    }

    const res = await apiCall(
      "PUT",
      HOMEWORK_SUBMISSION_ENDPOINT,
      payload,
      "Admin - Execute Submission Override & Audit Notes",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Admin structural adjustments rejected by server data sync layer",
      };
    }

    return {
      success: true,
      message: res.message || "Admin system override metrics synchronization clean",
    };
  } catch (error) {
    console.error("Admin Submission Modification Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected admin pipeline operational mismatch",
    };
  }
};